import { useState } from "react";
import { Link } from "react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@nosible/shared/components/ui/table";
import { Badge } from "@nosible/shared/components/ui/badge";
import { Button } from "@nosible/shared/components/ui/button";
import { trpc } from "@/admin/lib/trpc";
import type { AppRouter } from "@nosible/api";
import type { inferRouterOutputs } from "@trpc/server";

type RouterOutputs = inferRouterOutputs<AppRouter>;
type Subscription = RouterOutputs["admin"]["listSubscriptions"]["subscriptions"][number];

import { ChevronLeft, ChevronRight, Receipt, User, Building2, History } from "lucide-react";

export function AdminSubscriptions() {
  const [page, setPage] = useState(0);
  const pageSize = 10;

  const {
    data: subscriptionsData,
    isLoading,
    error,
  } = trpc.admin.listSubscriptions.useQuery(
    {
      limit: pageSize,
      offset: page * pageSize,
    },
    {
      enabled: true,
    }
  );

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-silica">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-red-400">Error loading subscriptions: {error.message}</div>
      </div>
    );
  }

  const totalPages = Math.ceil((subscriptionsData?.total ?? 0) / pageSize);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <Badge className="bg-neon-green text-obsidian">Active</Badge>;
      case "trialing":
        return <Badge className="bg-signal-blue">Trialing</Badge>;
      case "canceled":
        return <Badge variant="destructive">Canceled</Badge>;
      case "past_due":
        return <Badge variant="destructive">Past Due</Badge>;
      case "incomplete":
        return <Badge variant="secondary">Incomplete</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-2 mb-6">
        <Receipt className="h-6 w-6 text-violet-pulse" />
        <h1 className="text-2xl font-bold text-silica font-display">Admin: Subscriptions</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Subscriptions</CardTitle>
          <CardDescription>
            Manage all subscriptions. Total: {subscriptionsData?.total ?? 0} subscriptions
          </CardDescription>
        </CardHeader>
        <CardContent>
          {subscriptionsData?.subscriptions.length === 0 ? (
            <div className="text-center py-8 text-chromium-mist">
              <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No subscriptions found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Owner</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Usage</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subscriptionsData?.subscriptions.map((sub: Subscription) => (
                  <TableRow key={sub.id}>
                    <TableCell>
                      {sub.ownerId ? (
                        <div className="flex items-center gap-2">
                          {sub.ownerType === "organization" ? (
                            <Building2 className="h-4 w-4 text-violet-pulse" />
                          ) : (
                            <User className="h-4 w-4 text-signal-blue" />
                          )}
                          <Link
                            to={
                              sub.ownerType === "organization"
                                ? `/organizations/${sub.ownerId}`
                                : `/users/${sub.ownerId}`
                            }
                            className="text-silica hover:text-neon-green transition-colors"
                          >
                            {sub.ownerName || sub.ownerId}
                          </Link>
                        </div>
                      ) : (
                        <span className="text-chromium-mist">Unknown</span>
                      )}
                    </TableCell>
                    <TableCell className="font-medium text-silica">
                      <Link
                        to={`/plans/${sub.planId}`}
                        className="hover:text-neon-green transition-colors"
                      >
                        {sub.planId}
                      </Link>
                    </TableCell>
                    <TableCell>{getStatusBadge(sub.status)}</TableCell>
                    <TableCell>{sub.totalCount ?? 0}</TableCell>
                    <TableCell className="text-chromium-mist">
                      {sub.createdAt
                        ? new Date(sub.createdAt).toLocaleDateString()
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={`/subscriptions/${sub.id}`}>
                            View
                          </Link>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-core">
              <div className="text-sm text-chromium-mist">
                Page {page + 1} of {totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
